import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import useSWR from "swr";
import DownloadIcon from "@mui/icons-material/Download";
import { type FactoringImage } from "./factoringModel";
import FirebaseImage from "./FirebaseImage";
import { getJaDisplayName } from "./factoringUtil";
import { swrKey, factoringFetcher } from "./getFactoringApi";
import { deleteFactoringImage } from "./deleteFactoringImage";
import { downloadFactoringImages } from "./downloadFactoringImages";

interface ImageGalleryProps {
  factoringId: string;
}

export function FactoringImageGallery({ factoringId }: ImageGalleryProps) {
  const [selectedImages, setSelectedImages] = useState<FactoringImage[]>([]);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<FactoringImage | null>(
    null,
  );
  const [images, setImages] = useState<FactoringImage[]>([]);
  const [applicationNumber, setApplicationNumber] = useState<string>("");
  const [deletingImages, setDeletingImages] = useState<string[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading, error } = useSWR(
    [swrKey, factoringId],
    factoringFetcher,
  );

  useEffect(() => {
    if (data) {
      setImages(data.images);
      setApplicationNumber(data.applicationNumber);
      setUserId(data.userId);
    }
  }, [data]);

  // Group images by group property
  const groupedImages = images.reduce<Record<string, FactoringImage[]>>(
    (acc, image) => {
      if (!acc[image.group]) {
        acc[image.group] = [];
      }
      acc[image.group].push(image);
      return acc;
    },
    {},
  );

  // Sort by uploadTimingId
  const sortedGroupIds = Object.keys(groupedImages).sort().reverse();
  const sortedGroups = sortedGroupIds.map(
    (timingId) => groupedImages[timingId],
  );

  const handleCheckboxChange = (image: FactoringImage) => {
    setSelectedImages((prev) => {
      if (prev.some((i) => i.id === image.id)) {
        return prev.filter((i) => i.id !== image.id);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleDeleteClick = async (imageId: string, filePath: string) => {
    if (!confirm("画像を削除してもよろしいですか？")) {
      return;
    }

    if (!userId) {
      console.error("User ID is not available");
      alert("ユーザー情報が取得できません");
      return;
    }

    setDeletingImages((prev) => [...prev, imageId]);

    try {
      await deleteFactoringImage(userId, factoringId, imageId, filePath);

      setImages((prevImages) => prevImages.filter((image) => image.id !== imageId));
    } catch (error) {
      console.error("画像削除エラー:", error);
      alert("画像の削除中にエラーが発生しました");
    } finally {
      setDeletingImages((prev) => prev.filter((id) => id !== imageId));
    }
  };

  const handleDownloadSelected = async () => {
    setIsDownloading(true);
    try {
      await downloadFactoringImages(selectedImages);
    } catch (error) {
      console.error("画像のダウンロード中にエラーが発生しました:", error);
      alert("画像のダウンロード中にエラーが発生しました");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImageClick = (image: FactoringImage) => {
    setSelectedImage(image);
    setOpenImageModal(true);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 3,
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>データを読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: "error.main" }}>
        <Typography>
          エラーが発生しました: {error.message || "データの取得に失敗しました"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="div" gutterBottom>
          {getJaDisplayName("applicationNumber")} {applicationNumber}
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadSelected}
          disabled={selectedImages.length === 0 || isDownloading}
        >
          {isDownloading ? "ダウンロード中..." : "選択した画像をダウンロード"}
        </Button>
      </Box>

      {sortedGroups.map((group, groupIndex) => (
        <Box key={`group-${groupIndex}`}>
          {/* Add divider if not the first group */}
          {groupIndex > 0 && (
            <Box
              sx={{
                my: 3,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                width: "100%",
              }}
            />
          )}

          {/* Group header */}
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            必要書類画像 ({sortedGroups.length - groupIndex})
          </Typography>

          <Grid container spacing={2}>
            {group.map((image) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={image.id}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      display: "flex",
                      background: "rgba(255, 255, 255, 0.7)",
                      zIndex: 1,
                    }}
                  >
                    {deletingImages.includes(image.id) ? (
                      <Box sx={{ p: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Button
                        color="error"
                        variant="text"
                        onClick={() => handleDeleteClick(image.id, image.filePath)}
                      >
                        削除
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      pt: "75%", // 4:3 aspect ratio
                      position: "relative",
                      mb: 1,
                    }}
                    onClick={() => handleImageClick(image)}
                  >
                    <FirebaseImage
                      storagePath={image.filePath}
                      alt={image.fileName}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        cursor: "pointer",
                        opacity: deletingImages.includes(image.id) ? 0.5 : 1,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selectedImages.some((i) => i.id === image.id)}
                      onChange={() => handleCheckboxChange(image)}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {image.fileName}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Image Modal */}
      <Modal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        aria-labelledby="image-modal"
        aria-describedby="enlarged-image"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <div>
            {selectedImage && (
              <FirebaseImage
                storagePath={selectedImage.filePath}
                alt={selectedImage.fileName}
                style={{ maxWidth: "100%", maxHeight: "80vh" }}
              />
            )}
          </div>
          <Button
            sx={{ mt: 2 }}
            variant="contained"
            onClick={() => setOpenImageModal(false)}
          >
            閉じる
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}
